import { usePollingInterval } from 'src/hooks/usePollingInterval'
import { useEstimatorQuery, useQuoteQuery } from '@paintscout/api'
import { QuoteShim } from '@paintscout/react-quote'

import { getObjectLabels } from '@paintscout/util/builder'
import { Grid, Hidden, Skeleton, Typography, useClientOptions, useIsFirstRender } from '@paintscout/ui'
import { useApolloQueue } from 'shared/graphql'
import EditableQuote from 'src/components/common/EditableQuote/EditableQuote'
import { getQuoteRecoveryKey } from 'src/components/common/EditableQuote/quoteRecovery'
import BasePage from 'src/components/layouts/BasePage'

import { useSnackbar } from 'notistack'
import type { QuoteDocument } from 'paintscout'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useConnection } from 'react-detect-offline'
import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom'

import * as Sentry from '@sentry/react'

export default function EditQuote() {
  const match = useRouteMatch<{ id: string }>()
  const location = useLocation<{ newQuote?: QuoteDocument }>()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { id } = match.params

  const { online } = useConnection()
  const isFirstRender = useIsFirstRender()
  const { lastProcess } = useApolloQueue()

  const loadedQuote = useRef<QuoteDocument>(null)

  const { loading, data, error, refetch } = useQuoteQuery({
    pollInterval: usePollingInterval(150000),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: { id },
    onCompleted: (_) => {
      if (location.state) {
        delete location.state.newQuote
      }
    },
    onError: useCallback((e) => {
      // if the quote was already loaded we don't need to log the error
      if (!loadedQuote.current) {
        Sentry.captureException(e, { tags: { quoteId: id } })
      }
    }, [])
  })

  useEffect(() => {
    loadedQuote.current = data?.quote
  }, [data])

  // refetch quote after offline requests are sent
  useEffect(() => {
    if (lastProcess > 0 && online && !isFirstRender) {
      refetch()
    }
  }, [lastProcess, online])

  const recoveryData = useMemo(() => JSON.parse(localStorage.getItem(getQuoteRecoveryKey(id))), [])
  const quote = data?.quote ?? recoveryData?.quote

  // This is a transform of legacy quotes that will keep them working, but also looking as they did as legacy,
  // running this on all quotes, could useEffect this and only run if its older then x maybe? Or maybe mark transformed/new quotes and only run on old
  // console.log('OG quote', quote)
  // if (quote) {
  //   const adaptedQuote = transformLegacyQuote(quote, options)

  //   quote = adaptedQuote
  // }
  const { data: estimatorData } = useEstimatorQuery({
    skip: !quote?.owner?.id,
    fetchPolicy: 'network-only',
    variables: { id: quote?.owner?.id, quoteId: quote?._id }
  })
  const estimator = estimatorData?.estimator

  // For quotes beyond user permission that still get returned
  if (error && error.graphQLErrors?.[0]?.message === 'Not Accessible') {
    return <NotAccessible docType={quote?.is_invoice ? 'Invoice' : objectLabels.quote.value} />
  }

  // handle failure to load quote
  if (error && !quote && !recoveryData) {
    enqueueSnackbar(`${objectLabels.quote.value} failed to load`, {
      variant: 'error'
    })

    return <Redirect to="/quotes" />
  }

  // Only redirect if quote is permanently deleted (_deleted) or if it's trashed and past its deleteDate
  // Allow trashed quotes within the restoration window to be viewed so they can be restored
  if (quote?._deleted || (quote?.trashed && quote.deleteDate && quote.deleteDate < new Date().getTime())) {
    const quoteLabel = quote?.is_invoice
      ? getObjectLabels({ options, invoice: true }).quote.value
      : objectLabels.quote.value

    enqueueSnackbar(`${quoteLabel} was deleted and cannot be loaded`, {
      variant: 'error'
    })

    return <Redirect to="/quotes" />
  }

  if (!id) {
    return <Redirect to="/quotes" />
  }

  return (
    <>
      {!quote ? (
        <EditableQuoteShim />
      ) : (
        <EditableQuote
          estimator={estimator}
          quote={quote}
          recoveryData={recoveryData}
          refetch={refetch}
          refetching={!!quote && loading}
          onSaved={(savedQuote) => {
            if (savedQuote?.is_invoice !== history.location.pathname.includes('invoices')) {
              history.replace(`/${savedQuote.is_invoice ? 'invoices' : 'quotes'}/edit/${savedQuote._id}`, {
                skipDirtyPrompt: true
              })
            }
          }}
        />
      )}
    </>
  )
}

const NotAccessible = (props: { docType: string }) => {
  return (
    <BasePage>
      <Grid container spacing={3} direction="column" alignItems="center" justifyContent="center">
        <Grid item xs={8} sm={6} md={4}>
          <Typography variant={'h4'}>This {props.docType} is Not Accessible</Typography>
          <Typography>To view it, ask the {props.docType}&lsquo;s owner to add you for access.</Typography>
        </Grid>
      </Grid>
    </BasePage>
  )
}

const EditableQuoteShim = () => {
  return (
    <BasePage>
      <div style={{ height: 10, width: '100%' }} />
      {/* header */}
      <div style={{ maxWidth: '953px', margin: 'auto' }}>
        <Hidden smDown>
          <Skeleton height={'60px'} width={'100%'} />
          <div style={{ height: 10, width: '100%' }} />
          <Skeleton height={'40px'} width={'100%'} />
        </Hidden>
        <Hidden mdUp>
          <Skeleton height={'150px'} width={'100%'} />
        </Hidden>
      </div>
      <div style={{ height: 10, width: '100%' }} />
      <QuoteShim />
    </BasePage>
  )
}
